import { useState, useEffect, useMemo, useRef } from "react";
import { GoUpload } from "react-icons/go";
import { useSearchParams } from "react-router-dom";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import { BenefitDto, FREQUENCY } from "../../../../models/benefit.dto";
import { AiOutlinePlus } from "react-icons/ai";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import {
	useDeleteBenefitGroup,
	useGetBenefitGroup,
	useGetBenefitGroupSearch,
} from "../../../../services/benefit-group.service";
import {
	useDeleteBenefit,
	useGetBenefit,
	useGetBenefitSearch,
} from "../../../../services/benefit.service";
import { BenefitTypeOrderBy } from "../../../../models/benefit-type.dto";
import { useGetBenefitType } from "../../../../services/benefit-type.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import BenefitGroupModalForm from "../modal/BenefitGroupModalForm";
import BenefitModalForm from "../modal/BenefitModalForm";
import { useGetTaxCode } from "../../../../services/tax-code.service";
import { TaxCodeOrderBy } from "../../../../models/tax-code.dto";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import { useGetEmployee } from "../../../../services/employee.service";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetExplicitDeduction } from "../../../../services/explicit-deduction.service";
import { EmployeeBandOrderBy } from "../../../../models/employee-band.dto";
import { ExplicitDeductionOrderBy } from "../../../../models/explicit-deduction.dto";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import UploadBenefit from "../modal/UploadBenefit";
import { ChevronDown, Upload } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import {
	benefitGroupColumns,
	benefitGroupColumnVisibility,
	benefitColumns,
	benefitColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import ExportBenefitsModal from "../modal/ExportBenefitsModal";
import { APP_CONFIG } from "../../../../helpers/appHelpers";

const BenefitsView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<"benefitGroups" | "benefits">(
		"benefitGroups",
	);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openBenefitGroupModal, setOpenBenefitGroupModal] = useState(false);
	const [openBenefitModal, setOpenBenefitModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openUploadBenefitModal, setOpenUploadBenefitModal] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [isUploadExportOpen, setIsUploadExportOpen] = useState(false);
	const uploadExportDropdownRef = useRef<HTMLDivElement | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();
	// Column visibility for Benefit Groups
	const {
		columnVisibilityModel: benefitGroupColumnVisibilityModel,
		setColumnVisibilityModel: setBenefitGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(benefitGroupColumnVisibility);

	// Column visibility for Benefits
	const {
		columnVisibilityModel: benefitColumnVisibilityModel,
		setColumnVisibilityModel: setBenefitColumnVisibilityModel,
	} = useColumnVisibilityModel(benefitColumnVisibility);

	// Benefit Groups table
	const { mutate: searchBenefitGroups } = useGetBenefitGroupSearch(currentCompany?.id as number);
	const {
		tableAPIRef: benefitGroupTableAPIRef,
		fetchedTableData: fetchedBenefitGroupData,
		tableData: benefitGroupTableData,
		isLoading: benefitGroupLoading,
		pageCount: benefitGroupPageCount,
		setTableData: setBenefitGroupTableData,
	} = useManageTableWithSearchParams<BenefitDto>({
		fetchHook: (options) =>
			useGetBenefitGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Benefits table
	const { mutate: searchBenefits } = useGetBenefitSearch();
	const {
		tableAPIRef: benefitTableAPIRef,
		fetchedTableData: fetchedBenefitData,
		tableData: benefitTableData,
		isLoading: benefitLoading,
		pageCount: benefitPageCount,
		setTableData: setBenefitTableData,
	} = useManageTableWithSearchParams<BenefitDto>({
		fetchHook: useGetBenefit,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for forms
	const { data: benefitTypes } = useGetBenefitType({
		countryId: currentCompany?.countryId as number,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: BenefitTypeOrderBy.NAME_ASC,
	});
	const { data: taxCodes } = useGetTaxCode({
		countryId: currentCompany?.countryId as number,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: TaxCodeOrderBy.CODE_ASC,
	});

	const { data: benefitGroups } = useGetBenefitGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: employeeBands } = useGetEmployeeBand({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: EmployeeBandOrderBy.NAME_ASC,
	});
	const { data: explicitDeductions } = useGetExplicitDeduction({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: ExplicitDeductionOrderBy.NAME_ASC,
	});
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});

	// The Benefit Groups list/search/create/update endpoints only return
	// `benefitTypeInstanceId` (the foreign key) — they never include the
	// expanded `benefitTypeInstance: { name }` relation that the
	// "Benefit Type" column reads. Join it in on the client using the
	// benefit types already fetched for the form/filter dropdowns so the
	// column is populated regardless of where the row data came from
	// (initial load, search, create, or update).
	const enrichedBenefitGroupTableData = useMemo(() => {
		if (!benefitGroupTableData?.length) return benefitGroupTableData;

		return benefitGroupTableData.map((row: any) => {
			if (row?.benefitTypeInstance?.name) return row;

			const benefitTypeInstance = benefitTypes?.data?.find(
				(benefitType: any) => benefitType.id === row?.benefitTypeInstanceId,
			);

			return benefitTypeInstance ? { ...row, benefitTypeInstance } : row;
		});
	}, [benefitGroupTableData, benefitTypes]);

	const handleClose = () => setAnchorEl(null);

	const handleOpenBenefitGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenBenefitGroupModal(true);
	};

	const handleOpenBenefitModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenBenefitModal(true);
	};

	const handleTabChange = (tab: "benefitGroups" | "benefits") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteBenefitGroup } = useDeleteBenefitGroup();
	const { mutate: deleteBenefit } = useDeleteBenefit();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "benefitGroups") {
			deleteBenefitGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteBenefit(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				uploadExportDropdownRef.current &&
				!uploadExportDropdownRef.current.contains(event.target as Node)
			) {
				setIsUploadExportOpen(false);
			}
		};

		if (isUploadExportOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isUploadExportOpen]);

	const handleOpenExportModal = () => {
		setIsUploadExportOpen(false);
		setOpenExportModal(true);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={
							activeTab === "benefitGroups"
								? fetchedBenefitGroupData
								: fetchedBenefitData
						}
						setTableData={
							activeTab === "benefitGroups"
								? setBenefitGroupTableData
								: setBenefitTableData
						}
						mutate={
							activeTab === "benefitGroups"
								? searchBenefitGroups
								: searchBenefits
						}
					/>
					<TableFilter
						fields={
							activeTab === "benefitGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Tax Code",
											fieldName: "taxCode",
											fieldType: "INPUT_FIELD",
										},
										{
											title: "Frequency",
											fieldName: "frequency",
											fieldType: "CHECKBOX_LIST",
											options: FREQUENCY,
										},
										{
											title: "Benefit Type Instance",
											fieldName: "benefitTypeInstanceId",
											fieldType: "SELECT_FIELD",
											options: benefitTypes?.data || [],
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Tax Code",
											fieldName: "taxCode",
											fieldType: "INPUT_FIELD",
										},
										{
											title: "Frequency",
											fieldName: "frequency",
											fieldType: "CHECKBOX_LIST",
											options: FREQUENCY,
										},
										{
											title: "Benefit Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Benefit Type Instance",
											fieldName: "benefitTypeInstanceId",
											fieldType: "SELECT_FIELD",
											options: benefitTypes?.data || [],
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "benefitGroups"
								? (benefitGroupColumns as any)
								: (benefitColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "benefitGroups"
								? benefitGroupColumnVisibility
								: benefitColumnVisibility
						}
						visibilityModel={
							activeTab === "benefitGroups"
								? benefitGroupColumnVisibilityModel
								: benefitColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "benefitGroups"
								? setBenefitGroupColumnVisibilityModel
								: setBenefitColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "benefitGroups"
								? benefitGroupTableAPIRef
								: benefitTableAPIRef
						}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					{/* Upload/Export Dropdown - Only show for Employee Benefits tab */}
					{activeTab === "benefitGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenBenefitModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Benefit Group
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Allowances tab */}
					{activeTab === "benefits" && (
						<button
							onClick={handleOpenExportModal}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Benefits
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
					>
						{activeTab === "benefitGroups" ? (
							<button
								onClick={handleOpenBenefitGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Benefit Group
							</button>
						) : (
							<button
								onClick={handleOpenBenefitModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Benefit
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-6">
				<button
					onClick={() => handleTabChange("benefitGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "benefitGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Benefit Groups
				</button>
				<button
					onClick={() => handleTabChange("benefits")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "benefits"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Benefits
				</button>
			</div>

			{activeTab === "benefitGroups" ? (
				<TanStackEmployeeTable
					ref={benefitGroupTableAPIRef}
					tableId={TableId.BENEFIT_GROUPS}
					data={enrichedBenefitGroupTableData}
					columns={benefitGroupColumns}
					loading={benefitGroupLoading}
					columnVisibility={benefitGroupColumnVisibilityModel}
					onColumnVisibilityChange={setBenefitGroupColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={benefitGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={benefitGroupPageCount}
					apiRef={benefitGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={benefitTableAPIRef}
					tableId={TableId.BENEFITS}
					data={benefitTableData}
					columns={benefitColumns}
					loading={benefitLoading}
					columnVisibility={benefitColumnVisibilityModel}
					onColumnVisibilityChange={setBenefitColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={benefitColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={benefitPageCount}
					apiRef={benefitTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "benefitGroups"
						? setOpenBenefitGroupModal
						: setOpenBenefitModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openBenefitGroupModal && (
				<BenefitGroupModalForm
					open={openBenefitGroupModal}
					setOpen={setOpenBenefitGroupModal}
					title={isEditForm ? "Update Benefit Group" : "Add New Benefit Group"}
					isEditForm={isEditForm}
					tableAPIRef={benefitGroupTableAPIRef}
					benefitTypes={benefitTypes?.data || []}
					taxCodes={taxCodes?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openBenefitModal && (
				<BenefitModalForm
					open={openBenefitModal}
					setOpen={setOpenBenefitModal}
					title={isEditForm ? "Update Benefit" : "Add New Benefit"}
					isEditForm={isEditForm}
					tableAPIRef={benefitTableAPIRef}
					benefitTypes={benefitTypes?.data || []}
					taxCodes={taxCodes?.data || []}
					employees={employees?.data || []}
					employeeBands={employeeBands?.data || []}
					explicitDeductions={explicitDeductions?.data || []}
					payPeriods={payPeriods?.data || []}
					benefitGroups={benefitGroups?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadBenefitModal && (
				<UploadBenefit
					open={openUploadBenefitModal}
					setOpen={setOpenUploadBenefitModal}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportBenefitsModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</>
	);
};

export default BenefitsView;